import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, Building2, Loader2, CheckCircle, AlertCircle, Lock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../config/api';

// Use same-origin API URL to avoid CORS issues
const API = BACKEND_URL;

// Log API URL for debugging
console.log('EmbeddedPaymentForm API URL:', API);

// Initialize Stripe once at module level
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey && stripeKey !== 'pk_test_placeholder' 
  ? loadStripe(stripeKey) 
  : null;

if (!stripePromise) {
  console.error('Stripe publishable key not configured:', stripeKey ? 'Invalid key' : 'Key missing');
} else {
  console.log('Stripe initialized with key:', stripeKey?.substring(0, 20) + '...');
}

// Payment Method Selector Component
const PaymentMethodSelector = ({ methods, selected, onSelect, cartTotal }) => {
  // Check if Klarna is available (>= 35€)
  const klarnaAvailable = cartTotal >= 35;
  
  return (
    <div className="space-y-4">
      {/* Klarna Highlight Banner - Prominent when available */}
      {klarnaAvailable && (
        <div 
          onClick={() => onSelect('klarna')}
          className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
            selected === 'klarna'
              ? 'border-[#FFB3C7] bg-gradient-to-r from-[#FFB3C7]/20 to-[#FFB3C7]/5'
              : 'border-[#FFB3C7]/30 bg-[#FFB3C7]/5 hover:border-[#FFB3C7]/60'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFB3C7] flex items-center justify-center">
                <span className="font-black text-lg text-black">K.</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-white text-lg">Payer en 3x sans frais</p>
                  <span className="px-2 py-0.5 bg-[#FFB3C7] text-black text-xs font-bold rounded-full">POPULAIRE</span>
                </div>
                <p className="text-[#FFB3C7] font-medium">
                  3 × {(cartTotal / 3).toFixed(2)} € avec Klarna
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
              selected === 'klarna' ? 'border-[#FFB3C7] bg-[#FFB3C7]' : 'border-white/30'
            }`}>
              {selected === 'klarna' && (
                <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        </div>
      )}
      
      <p className="text-white/50 text-xs uppercase tracking-wider">Ou payer en une fois</p>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Card + Apple Pay */}
        <button
          onClick={() => onSelect('card')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            selected === 'card'
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-white/10 bg-white/[0.02] hover:border-white/20'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              selected === 'card' ? 'bg-emerald-500/20' : 'bg-white/5'
            }`}>
              <CreditCard className={`w-5 h-5 ${selected === 'card' ? 'text-emerald-400' : 'text-white/60'}`} />
            </div>
            <div>
              <p className={`font-medium ${selected === 'card' ? 'text-white' : 'text-white/80'}`}>
                Carte bancaire
              </p>
              <p className="text-xs text-white/40">CB, Visa, Mastercard</p>
            </div>
          </div>
          {/* Apple Pay indicator */}
          <div className="mt-2 flex items-center gap-1 text-white/40 text-xs">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span>Apple Pay disponible</span>
          </div>
        </button>

        {/* Klarna - Smaller option for those who scrolled */}
        {klarnaAvailable && (
          <button
            onClick={() => onSelect('klarna')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              selected === 'klarna'
                ? 'border-[#FFB3C7] bg-[#FFB3C7]/10'
                : 'border-white/10 bg-white/[0.02] hover:border-[#FFB3C7]/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selected === 'klarna' ? 'bg-[#FFB3C7]' : 'bg-[#FFB3C7]/20'
              }`}>
                <span className={`font-black text-sm ${selected === 'klarna' ? 'text-black' : 'text-[#FFB3C7]'}`}>K.</span>
              </div>
              <div>
                <p className={`font-medium ${selected === 'klarna' ? 'text-white' : 'text-white/80'}`}>
                  Klarna 3x
                </p>
                <p className="text-xs text-[#FFB3C7]">Sans frais</p>
              </div>
            </div>
          </button>
        )}

        {/* SEPA */}
        <button
          onClick={() => onSelect('sepa_debit')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            selected === 'sepa_debit'
              ? 'border-blue-500 bg-blue-500/10'
              : 'border-white/10 bg-white/[0.02] hover:border-white/20'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              selected === 'sepa_debit' ? 'bg-blue-500/20' : 'bg-white/5'
            }`}>
              <Building2 className={`w-5 h-5 ${selected === 'sepa_debit' ? 'text-blue-400' : 'text-white/60'}`} />
            </div>
            <div>
              <p className={`font-medium ${selected === 'sepa_debit' ? 'text-white' : 'text-white/80'}`}>
                Prélèvement SEPA
              </p>
              <p className="text-xs text-white/40">Débit bancaire</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

// Checkout Form Component (inside Elements provider)
const CheckoutForm = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setMessage(error.message);
        onError?.(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded without redirect
        onSuccess?.(paymentIntent);
      } else if (paymentIntent && paymentIntent.status === 'processing') {
        setMessage("Paiement en cours de traitement...");
      }
    } catch (err) {
      setMessage("Une erreur est survenue. Veuillez réessayer.");
      onError?.(err);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stripe Payment Element */}
      <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
        <PaymentElement 
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                address: {
                  country: 'FR'
                }
              }
            }
          }}
        />
      </div>

      {/* Error Message */}
      {message && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="w-full h-14 bg-emerald-500 hover:bg-emerald-400 text-white text-lg font-semibold rounded-xl transition-all"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Traitement en cours...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Payer {amount.toFixed(2)} €
          </>
        )}
      </Button>

      {/* Security Badges */}
      <div className="flex items-center justify-center gap-4 pt-2">
        <div className="flex items-center gap-1.5 text-white/40 text-xs">
          <Shield className="w-4 h-4" />
          <span>Paiement sécurisé SSL</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/40 text-xs">
          <CheckCircle className="w-4 h-4" />
          <span>Certifié PCI-DSS</span>
        </div>
      </div>
    </form>
  );
};

// Main Embedded Payment Component
const EmbeddedPaymentForm = ({ 
  cartTotal, 
  cartId,
  onPaymentSuccess, 
  onPaymentError,
  hasSubscriptions = false
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Create PaymentIntent when component mounts
    const createPaymentIntent = async () => {
      // Check if Stripe is configured
      if (!stripePromise) {
        setError("Stripe n'est pas configuré. Veuillez contacter le support.");
        setIsLoading(false);
        return;
      }
      
      // Avoid re-creating if we already have a clientSecret
      if (clientSecret) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);

        console.log('Creating PaymentIntent for cart:', cartId, 'total:', cartTotal);
        
        const response = await fetch(`${API}/api/checkout/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            cart_id: cartId,
            payment_method_type: selectedMethod
          })
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Erreur lors de la création du paiement');
        }

        if (!data.client_secret) {
          throw new Error('Réponse invalide du serveur');
        }

        console.log('PaymentIntent created successfully:', data.payment_intent_id);
        setClientSecret(data.client_secret);
        
      } catch (err) {
        console.error('PaymentIntent error:', err);
        setError(err.message || 'Erreur inconnue');
      } finally {
        setIsLoading(false);
      }
    };

    if (cartId && cartTotal > 0 && !clientSecret) {
      createPaymentIntent();
    } else if (!cartId || cartTotal <= 0) {
      setError("Panier invalide");
      setIsLoading(false);
    }
  }, [cartId, cartTotal, selectedMethod, clientSecret]);

  const handleSuccess = async (paymentIntent) => {
    // Confirm payment on backend
    try {
      const response = await fetch(`${API}/api/checkout/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payment_intent_id: paymentIntent.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        onPaymentSuccess?.(data);
      }
    } catch (err) {
      console.error('Confirm error:', err);
    }
  };

  const appearance = useMemo(() => ({
    theme: 'night',
    variables: {
      colorPrimary: '#10b981',
      colorBackground: '#0a0a0a',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '12px',
      fontSizeBase: '16px'
    },
    rules: {
      '.Input': {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: 'none'
      },
      '.Input:focus': {
        border: '1px solid #10b981',
        boxShadow: '0 0 0 1px #10b981'
      },
      '.Label': {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px'
      },
      '.Tab': {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      },
      '.Tab--selected': {
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid #10b981'
      },
      '.TabIcon': {
        fill: '#ffffff'
      },
      '.TabLabel': {
        color: '#ffffff'
      }
    }
  }), []);

  const options = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance,
      locale: 'fr'
    };
  }, [clientSecret, appearance]);

  // Early returns AFTER all hooks - show loading immediately
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mb-3" />
        <p className="text-white/60 text-sm">Préparation du paiement sécurisé...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 font-medium mb-2">Erreur</p>
        <p className="text-white/60 text-sm">{error}</p>
        <Button
          onClick={() => window.location.reload()}
          className="mt-4 bg-white/10 hover:bg-white/20"
        >
          Réessayer
        </Button>
      </div>
    );
  }

  if (!clientSecret || !options) {
    return (
      <div className="space-y-6">
        <div className="text-center pb-4 border-b border-white/10">
          <h2 className="text-2xl font-semibold text-white mb-1">Paiement sécurisé</h2>
          <p className="text-white/50">Chargement du formulaire de paiement...</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      </div>
    );
  }

  // Vérifier que stripePromise n'est pas null
  if (!stripePromise) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 font-medium mb-2">Configuration Stripe manquante</p>
        <p className="text-white/60 text-sm">Veuillez contacter le support technique.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-4 border-b border-white/10">
        <h2 className="text-2xl font-semibold text-white mb-1">Paiement sécurisé</h2>
        <p className="text-white/50">Vos données sont protégées par cryptage SSL</p>
      </div>

      {/* Stripe Elements - use key to force remount on clientSecret change */}
      <Elements 
        stripe={stripePromise} 
        options={options} 
        key={`stripe-elements-${clientSecret}`}
      >
        <CheckoutForm 
          amount={cartTotal}
          onSuccess={handleSuccess}
          onError={onPaymentError}
        />
      </Elements>
    </div>
  );
};

export default EmbeddedPaymentForm;
